// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePageSize from "@/hooks/usePageSize";
import usePermission from "@/hooks/usePermission";
import useBulk from "@/hooks/useBulk";
import usePopup from "@/hooks/usePopup";
import useSelection from "@/hooks/useSelection";
import useShare from "@/hooks/useShare";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyLangValue, TyLead, TyProject, TyUnit } from "@/types";
import { formatDate, readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import StatusChip from "@/UI/atoms/StatusChip";
import LeadCard from "@/UI/cards/LeadCard";
import UnitCard from "@/UI/cards/UnitCard";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import type { TyBulkField } from "@/UI/components/BulkBar/types";
import Paginator from "@/UI/components/Paginator";
import AddMenuButton from "@/UI/components/AddMenuButton";
import EmptyState from "@/UI/components/EmptyState";
import RecordFiles from "@/UI/components/RecordFiles";
import RecordPanel from "@/UI/components/RecordPanel";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import MultiSelectInput from "@/UI/inputs/MultiSelectInput";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import BulkUnitsPopup from "@/UI/popups/BulkUnitsPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import HistoryPopup from "@/UI/popups/HistoryPopup";
import LeadFormPopup from "@/UI/popups/LeadFormPopup";
import BulkFieldPopup from "@/UI/popups/BulkFieldPopup";
import ProjectFormPopup from "@/UI/popups/ProjectFormPopup";
import RenamePopup from "@/UI/popups/RenamePopup";
import UnitFieldsPopup, { type TyUnitFieldValues } from "@/UI/popups/UnitFieldsPopup";
import UnitFormPopup from "@/UI/popups/UnitFormPopup";
import UploadFilesPopup from "@/UI/popups/UploadFilesPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type TyTab = "details" | "units" | "interests" | "buildings" | "files";

/**
 * One project: its head, the numbers it is judged by, and its heavy children behind tabs — the head
 * and the bar being one card, because they are one thing.
 *
 * A project is not a form. The details live in the edit panel and in their own tab; this screen is
 * for the units underneath, which is what anyone opens it for.
 *
 * The tab lives in the URL and **replaces**, so moving between tabs never stacks history a person has
 * to press back through to leave the project.
 */
export default function ProjectScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const { canShare, share } = useShare();
    const { slug = "" } = useParams();
    const addComp = usePopup((state) => state.addComp);
    const { role } = usePermission();
    const uploadsFinishedAt = useUploads((state) => state.finishedAt);
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    const tab = (params.get("tab") as TyTab) || "units";
    const search = params.get("search") ?? "";
    const statuses = (params.get("status") ?? "").split(",").filter(Boolean);
    const types = (params.get("type") ?? "").split(",").filter(Boolean);
    const page = Math.max(1, Number(params.get("page")) || 1);

    const [isFiltering, setIsFiltering] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search);
    const [draftStatuses, setDraftStatuses] = useState<string[]>(statuses);
    const [draftTypes, setDraftTypes] = useState<string[]>(types);

    const isFiltered = !!search || statuses.length > 0 || types.length > 0;
    const project = useFetch<{ project: TyProject }>(`/properties/projects/${slug}`);
    const units = useFetch<{ units: TyUnit[]; total: number }>("/properties/units", {}, false);
    /** Who wants this development. The pipeline's own list, filtered to this project. */
    const leads = useFetch<{ leads: TyLead[] }>("/crm/leads", {}, false);
    const action = useFetch("/properties", {}, false);
    /** Its own call: once the project is in the trash there is nothing here to reload. */
    const removal = useFetch(`/properties/projects/${slug}`, { method: "DELETE" }, false);
    const selection = useSelection();

    const loadUnits = () => {
        const query: Record<string, unknown> = { project: slug, limit: pageSize, page };
        if (search.trim()) query.search = search.trim();
        if (statuses.length) query.status = statuses.join(",");
        if (types.length) query.type = types.join(",");
        units.invoke({ query });
    };

    const bulk = useBulk({ path: "/properties/units", selection, onDone: () => loadUnits() });

    useEffect(() => {
        if (tab === "units") loadUnits();
        // A narrowed, paged or switched list is a different list, so its picks go with it.
        selection.clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, slug, search, params.get("status"), params.get("type"), page, pageSize]);

    const projectId = project.data?.project._id;

    useEffect(() => {
        if (tab !== "interests" || !projectId) return;
        leads.invoke({ query: { project: projectId, closed: "all", limit: constants.maxPageSize } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, projectId]);

    const refresh = () => {
        project.invoke();
        if (tab === "units") loadUnits();
    };

    useEffect(() => {
        if (!action.succeededAt) return;
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    useEffect(() => {
        if (!uploadsFinishedAt) return;
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadsFinishedAt]);

    useEffect(() => {
        if (!removal.succeededAt) return;
        navigate("/properties", { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removal.succeededAt]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        // Narrowing a list — or changing tab — puts you back at its start.
        if (!("page" in next)) merged.delete("page");
        setParams(merged, { replace: true });
    };

    const record = project.data?.project;
    const name = record ? readLangValue(record.name, lang) || t.properties.unnamed : "";

    const openUnitForm = (unit?: TyUnit, isDuplicate = false) =>
        record &&
        addComp({
            name: "unit-form",
            Component: UnitFormPopup,
            place: "end",
            anim: "endSlide",
            details: { unit: unit ?? null, isDuplicate, project: record, onDone: refresh },
        });

    /** The one destructive action, shared by the header's trash and the menu that can replace it. */
    const confirmRemove = () =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.common.remove,
                message: t.properties.confirmRemoveProject.replace("{name}", name),
                confirmLabel: t.common.remove,
                onConfirm: () => removal.invoke(),
            },
        });

    const openUnitActions = (unit: TyUnit, anchor: DOMRect) => {
        // No "open" entry: the whole card already does that, and offering it again took the first
        // row of every menu to repeat the one thing the card is for.
        const actions: TyAction[] = [
            { key: "edit", label: t.common.edit, icon: "edit", onSelect: () => openUnitForm(unit) },
            // Reads the unit and opens a new one filled in with it. The number is the only field left
            // empty, because it is the only one that has to differ.
            { key: "duplicate", label: t.properties.duplicateUnit, icon: "copy", onSelect: () => openUnitForm(unit, true) },
            /* How a selection starts. Adding to it afterwards is a press on the card itself. */
            { key: "select", label: t.bulk.select, icon: "check", onSelect: () => selection.toggle(unit._id) },
            /* Only where there is a page to link to: an unpublished unit answers 404. */
            ...(unit.isPublic && record?.isPublic && canShare
                ? [
                      {
                          key: "share",
                          label: t.common.share,
                          icon: "copy" as const,
                          onSelect: () => share(`${t.properties.fields.number} ${unit.number}`, record.slug, unit.number),
                      },
                  ]
                : []),
            {
                key: "remove",
                label: t.common.remove,
                icon: "trash",
                danger: true,
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: t.common.remove,
                            message: t.properties.confirmRemoveUnit.replace("{name}", unit.number),
                            confirmLabel: t.common.remove,
                            onConfirm: () => action.invoke({ method: "DELETE", path: `/properties/units/${unit._id}` }),
                        },
                    }),
            },
        ];

        addComp({
            name: "unit-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    /** The upload action lives with the bar's add button, pointed at whichever record is on screen. */
    const openUpload = (model: string, ownerId: string) =>
        addComp({
            name: "upload-files",
            Component: UploadFilesPopup,
            place: "end",
            anim: "endSlide",
            details: { owner: { model, id: ownerId } },
        });

    /**
     * Add a block without leaving whatever is on screen. `onCreated` is how the bulk bar's block
     * select picks the new one the moment it exists — the created entry is the one the project did
     * not have a second ago.
     */
    const openAddBuilding = (onCreated?: (id: string) => void) =>
        record &&
        addComp({
            name: "add-building",
            Component: RenamePopup,
            anim: "scale",
            details: {
                title: t.properties.addBuilding,
                value: "",
                path: `/properties/projects/${record._id}/buildings`,
                method: "POST",
                onDone: (data: unknown) => {
                    const next = (data as { buildings?: { _id: string; name: string }[] } | undefined)?.buildings;
                    const created = next?.find((entry) => !(record.buildings ?? []).some((old) => old._id === entry._id));
                    refresh();
                    if (created) onCreated?.(created._id);
                },
            },
        });

    /** The pipeline's own form, with this development already filled in. */
    const openAddLead = () =>
        record &&
        addComp({
            name: "lead-form",
            Component: LeadFormPopup,
            place: "end",
            anim: "endSlide",
            details: {
                projectId: record._id,
                onDone: () => leads.invoke({ query: { project: record._id, closed: "all", limit: constants.maxPageSize } }),
            },
        });

    /**
     * Buildings sit before the interests: the first three tabs are the development itself — what it
     * is, what is in it, how it is divided — and who wants it is a different question that follows
     * them rather than interrupting them.
     */
    const tabs = [
        { key: "details", label: t.properties.tabs.details },
        { key: "units", label: t.properties.tabs.units },
        { key: "buildings", label: t.properties.tabs.buildings },
        { key: "interests", label: t.crm.interests },
        { key: "files", label: t.properties.tabs.files },
    ];

    if (project.loading && !record) return <p className="project-screen__state">{t.common.loading}</p>;
    if (project.error) return <p className="project-screen__state project-screen__state--error">{project.error}</p>;
    if (!record) return null;

    /**
     * The bar's own editors for the three fields that will not fit in a row — a group of thirty
     * marks, a bilingual description, a point on a map.
     *
     * Both are handed the draft's current value and a way to write it back, so the bar stays the one
     * owner of what is about to be sent and this screen only owns the wording and the popup.
     */
    const openBulkGroup = (kind: "counts" | "flags", current: unknown, commit: (value: unknown) => void) =>
        addComp({
            name: `bulk-${kind}`,
            Component: UnitFieldsPopup,
            anim: "scale",
            details: {
                title: kind === "counts" ? t.properties.groups.counts : t.properties.groups.flags,
                keys: constants.unitGroups[kind],
                labels: kind === "counts" ? t.properties.counts : t.properties.flags,
                kind,
                values: (current as TyUnitFieldValues) ?? {},
                /**
                 * **Empty, and `partial`.** Nothing is being written until somebody answers something
                 * here, so every answer reads as a change and can be taken back one at a time — and
                 * only the answers come back. The whole group would blank every room and feature on
                 * every unit in the selection, which is the one mistake here a screen could not undo.
                 */
                baseline: {},
                partial: true,
                onConfirm: (values: TyUnitFieldValues) => commit(Object.keys(values).length ? values : undefined),
            },
        });

    const openBulkField = (key: "description" | "notes" | "location", current: unknown, commit: (value: unknown) => void) =>
        addComp({
            name: `bulk-${key}`,
            Component: BulkFieldPopup,
            anim: "scale",
            details: {
                title: t.properties.fields[key],
                kind: key === "location" ? "location" : key === "description" ? "lang" : "text",
                value: current,
                hint: t.bulk.fieldHint,
                onConfirm: (value: unknown) => commit(value),
                onClear: () => commit(undefined),
            },
        });

    /** A group button's brief: how many marks it will write, or that it will empty the field. */
    const countBrief = (value: unknown) => {
        const answered = value && typeof value === "object" ? Object.keys(value as object).length : 0;
        return answered ? t.bulk.groupSet.replace("{count}", String(answered)) : t.bulk.groupCleared;
    };
    const langBrief = (value: unknown) =>
        (Array.isArray(value) ? (value as TyLangValue[]) : []).some((entry) => entry.value?.trim()) ? t.bulk.written : t.bulk.groupCleared;
    const textBrief = (value: unknown) => (String(value ?? "").trim() ? t.bulk.written : t.bulk.groupCleared);
    const pointBrief = (value: unknown) => (value ? t.bulk.pointPicked : t.bulk.groupCleared);

    const yesNo = [
        { value: "1", label: t.properties.yes },
        { value: "0", label: t.properties.no },
    ];

    /**
     * What a floor of flats genuinely shares — which is nearly everything. **The number and the deed
     * are absent and the API refuses them**: a number identifies the unit inside its project and a
     * deed is one piece of paper per unit, so a bulk setter for either is a way to lose a floor. See
     * `ai.plans/bulk-edit-feature.md` §2d.
     *
     * Every box is the bar's one width — see `BulkBar/styles.css` for why 120px, and why a flag is
     * the exception that sizes to its own label instead.
     */
    const bulkFields: TyBulkField[] = [
        {
            key: "status",
            label: t.properties.fields.status,
            kind: "select",
            options: constants.unitStatuses.map((item) => ({ value: item, label: t.properties.statuses[item] })),
        },
        {
            key: "type",
            label: t.properties.fields.type,
            kind: "select",
            options: [
                { value: "", label: t.properties.notSet },
                ...constants.unitTypes.map((item) => ({ value: item, label: t.properties.unitTypes[item] })),
            ],
        },
        {
            key: "usage",
            label: t.properties.fields.usage,
            kind: "select",
            options: [
                { value: "", label: t.properties.notSet },
                ...constants.unitUsages.map((item) => ({ value: item, label: t.properties.usages[item] })),
            ],
        },
        {
            key: "offer",
            label: t.properties.fields.offer,
            kind: "select",
            options: [
                { value: "", label: t.properties.notSet },
                ...constants.unitOffers.map((item) => ({ value: item, label: t.properties.offers[item] })),
            ],
        },
        // A rent period means nothing under a sale, so it is only offered once rent is the answer.
        {
            key: "rentPeriod",
            label: t.properties.fields.rentPeriod,
            kind: "number",
            suffix: t.properties.months,
            when: (draft) => draft.offer === "rent",
        },
        { key: "price", label: t.properties.fields.price, kind: "number", suffix: t.properties.riyal },
        {
            key: "building",
            label: t.properties.fields.building,
            kind: "select",
            // A floor of units usually needs the block that does not exist yet, so it is made here.
            onAdd: (commit) => openAddBuilding(commit),
            addLabel: t.properties.addBuilding,
            options: [
                { value: "", label: t.properties.notSet },
                ...(record.buildings ?? []).map((building) => ({ value: building._id, label: building.name })),
            ],
        },
        { key: "floor", label: t.properties.fields.floor, kind: "number" },

        // The areas, one field each: a dotted key is nested on the way out, so writing the built area
        // leaves the plot and the street alone. The whole `measures` object would not.
        ...constants.unitGroups.measures.map((key) => ({
            key: `measures.${key}`,
            label: t.properties.measures[key],
            kind: "number" as const,
        })),

        {
            key: "choices.facades",
            label: t.properties.choices.facades,
            kind: "multi",
            options: constants.unitFacades.map((item) => ({ value: item, label: t.properties.facades[item] })),
        },
        {
            key: "choices.acSystem",
            label: t.properties.choices.acSystem,
            kind: "select",
            options: [
                { value: "", label: t.properties.notSet },
                ...constants.acSystems.map((item) => ({ value: item, label: t.properties.acSystems[item] })),
            ],
        },

        // Boxes rather than dropdowns, each as wide as its own sentence. The × beside a ticked one is
        // what says "leave these records alone", which an unticked box cannot.
        { key: "isPublic", label: t.properties.fields.isPublic, kind: "check", options: yesNo },
        { key: "useProjectGallery", label: t.properties.fields.useProjectGallery, kind: "check", options: yesNo },

        // The five that need a popup, each saying on its face what it will write.
        {
            key: "counts",
            label: t.properties.groups.counts,
            kind: "group",
            brief: countBrief,
            open: (current, commit) => openBulkGroup("counts", current, commit),
        },
        {
            key: "flags",
            label: t.properties.groups.flags,
            kind: "group",
            brief: countBrief,
            open: (current, commit) => openBulkGroup("flags", current, commit),
        },
        {
            key: "description",
            label: t.properties.fields.description,
            kind: "group",
            brief: langBrief,
            open: (current, commit) => openBulkField("description", current, commit),
        },
        {
            key: "location",
            label: t.properties.fields.location,
            kind: "group",
            brief: pointBrief,
            open: (current, commit) => openBulkField("location", current, commit),
        },
        {
            key: "notes",
            label: t.properties.fields.notes,
            kind: "group",
            brief: textBrief,
            open: (current, commit) => openBulkField("notes", current, commit),
        },
    ];

    /** One add button per tab, even where there are two ways to add. */
    const addOptions = () => {
        if (tab === "units") {
            return {
                label: t.common.add,
                options: [
                    { key: "one", label: t.properties.addUnit, icon: "add" as const, onSelect: () => openUnitForm() },
                    {
                        key: "many",
                        label: t.properties.addUnits,
                        icon: "copy" as const,
                        onSelect: () =>
                            record &&
                            addComp({
                                name: "bulk-units",
                                Component: BulkUnitsPopup,
                                place: "end",
                                anim: "endSlide",
                                details: { project: record, onDone: refresh },
                            }),
                    },
                ],
            };
        }
        if (tab === "buildings")
            return {
                label: t.common.add,
                options: [{ key: "one", label: t.properties.addBuilding, icon: "building" as const, onSelect: openAddBuilding }],
            };
        if (tab === "interests")
            return {
                label: t.common.add,
                options: [{ key: "one", label: t.crm.addLead, icon: "crm" as const, onSelect: openAddLead }],
            };
        if (tab === "files") {
            return {
                label: t.drive.upload,
                options: [{ key: "upload", label: t.drive.upload, icon: "upload" as const, onSelect: () => openUpload("Project", record._id) }],
            };
        }
        return null;
    };

    const add = addOptions();

    /**
     * Only what somebody filled in. A grid of em-dashes is a page the length of a full one that says
     * nothing, and a blank is already visible where it belongs — in the edit panel.
     */
    const allFacts: { key: string; icon: TyIconName; label: string; value: string | null; dir?: string }[] = [
        { key: "city", icon: "building", label: t.properties.fields.city, value: record.city },
        { key: "address", icon: "properties", label: t.properties.fields.address, value: record.address },
        { key: "slug", icon: "eye", label: t.properties.fields.slug, value: record.slug, dir: "ltr" },
        // The three registry numbers share a drawing on purpose: they are the same kind of fact,
        // and inventing a different icon for each would say they are not.
        { key: "planNumber", icon: "document", label: t.properties.fields.planNumber, value: record.planNumber },
        { key: "plotNumber", icon: "document", label: t.properties.fields.plotNumber, value: record.plotNumber },
        { key: "wafiNumber", icon: "document", label: t.properties.fields.wafiNumber, value: record.wafiNumber },
        { key: "startedAt", icon: "calendar", label: t.properties.fields.startedAt, value: formatDate(record.startedAt, lang) },
        { key: "deliveryDate", icon: "calendar", label: t.properties.fields.deliveryDate, value: formatDate(record.deliveryDate, lang) },
        // A flag always has an answer, so it is never the blank kind of empty.
        { key: "isPublic", icon: "eye", label: t.properties.fields.isPublic, value: record.isPublic ? t.properties.yes : t.properties.no },
    ];

    const facts = allFacts.filter((fact) => fact.value);

    const description = readLangValue(record.description, lang);
    const hasDetails = facts.length > 0 || !!record.location || !!description || !!record.notes;

    return (
        <section className="project-screen">
            <RecordPanel
                title={name}
                subtitle={[record.city, record.address].filter(Boolean).join(" · ")}
                chips={
                    <>
                        {record.stage && <StatusChip tone="accent" size="sm" label={t.properties.stages[record.stage]} />}
                        {record.types.map((type) => (
                            <StatusChip key={type} tone="neutral" size="sm" label={t.properties.unitTypes[type]} />
                        ))}
                        {record.incomplete && <StatusChip tone="reserved" size="sm" label={t.properties.incomplete} />}
                    </>
                }
                onHistory={
                    role === "member"
                        ? undefined
                        : () =>
                              addComp({
                                  name: "history",
                                  Component: HistoryPopup,
                                  place: "end",
                                  anim: "endSlide",
                                  details: { title: name, path: `/properties/projects/${record._id}/updates` },
                              })
                }
                onEdit={() =>
                    addComp({
                        name: "project-form",
                        Component: ProjectFormPopup,
                        place: "end",
                        anim: "endSlide",
                        details: { project: record, onDone: refresh },
                    })
                }
                /*
                 * Published, so there is a link to share — and `onMore` replaces the header's own
                 * trash, which is why the delete moves in beside it rather than sitting outside a
                 * menu with one item in it.
                 */
                onMore={
                    record.isPublic && canShare
                        ? (anchor) =>
                              addComp({
                                  name: "project-actions",
                                  Component: ActionsPopup,
                                  place: "anchor-below",
                                  anim: "scaleTop",
                                  cover: "invisible",
                                  details: {
                                      actions: [
                                          { key: "share", label: t.common.share, icon: "copy", onSelect: () => share(name, record.slug) },
                                          { key: "remove", label: t.common.remove, icon: "trash", danger: true, onSelect: confirmRemove },
                                      ] as TyAction[],
                                  },
                                  anchor: {
                                      top: anchor.top,
                                      left: anchor.left,
                                      right: anchor.right,
                                      bottom: anchor.bottom,
                                      width: anchor.width,
                                      height: anchor.height,
                                  },
                              })
                        : undefined
                }
                onRemove={record.isPublic && canShare ? undefined : confirmRemove}
            >
                <ActionsBar
                    tabs={tabs}
                    activeTab={tab}
                    onTab={(key) => apply({ tab: key })}
                    // The filter belongs to the units tab, so it is only offered there.
                    isFilterOpen={isFiltering}
                    onFilterOpen={
                        tab === "units"
                            ? () => {
                                  setDraftSearch(search);
                                  setDraftStatuses(statuses);
                                  setDraftTypes(types);
                                  setIsFiltering(true);
                              }
                            : undefined
                    }
                    hasFilters={isFiltered}
                    onFilterApply={() => {
                        setIsFiltering(false);
                        apply({ search: draftSearch.trim() || null, status: draftStatuses.join(",") || null, type: draftTypes.join(",") || null });
                    }}
                    onFilterClear={() => {
                        setDraftSearch("");
                        setDraftStatuses([]);
                        setDraftTypes([]);
                        setIsFiltering(false);
                        apply({ search: null, status: null, type: null });
                    }}
                    onFilterCancel={() => setIsFiltering(false)}
                    filterContent={
                        tab === "units" ? (
                            <>
                                <FilterSearchInput
                                    value={draftSearch}
                                    onChange={setDraftSearch}
                                    label={t.common.search}
                                    placeholder={t.properties.searchUnits}
                                />
                                <MultiSelectInput
                                    compact
                                    value={draftStatuses}
                                    onChange={setDraftStatuses}
                                    options={constants.unitStatuses.map((item) => ({ value: item, label: t.properties.statuses[item] }))}
                                    label={t.properties.fields.status}
                                />
                                <MultiSelectInput
                                    compact
                                    value={draftTypes}
                                    onChange={setDraftTypes}
                                    options={constants.unitTypes.map((item) => ({ value: item, label: t.properties.unitTypes[item] }))}
                                    label={t.properties.fields.type}
                                />
                            </>
                        ) : undefined
                    }
                    after={add ? <AddMenuButton name="project-add" label={add.label} options={add.options} compact /> : undefined}
                />
            </RecordPanel>

            {(action.error || removal.error) && <p className="project-screen__state project-screen__state--error">{action.error ?? removal.error}</p>}

            {/* --- Units ---------------------------------------------------------------------- */}
            {tab === "units" && (
                <>
                    {units.loading && <p className="project-screen__state">{t.common.loading}</p>}
                    {!units.loading && !units.data?.units.length && (
                        <EmptyState
                            icon="building"
                            title={t.properties.emptyUnits}
                            description={isFiltered ? t.properties.emptyFiltered : t.properties.emptyUnitsHint}
                            actionLabel={isFiltered ? undefined : t.properties.addUnit}
                            onAction={isFiltered ? undefined : () => openUnitForm()}
                        />
                    )}

                    <div className="project-screen__grid">
                        {units.data?.units.map((unit) => (
                            <UnitCard
                                key={unit._id}
                                unit={unit}
                                isSelected={selection.has(unit._id)}
                                isPicking={selection.isSelecting}
                                // While the bar is up the card picks rather than opens — the gesture
                                // every file manager already taught people.
                                onOpen={() =>
                                    selection.isSelecting
                                        ? selection.toggle(unit._id)
                                        : navigate(`/properties/${slug}/units/${encodeURIComponent(unit.number)}`)
                                }
                                onActions={(anchor) => openUnitActions(unit, anchor)}
                            />
                        ))}
                    </div>

                    {/* One or the other, never both — `hooks/useSelection` says why the pager goes. */}
                    {selection.isSelecting ? (
                        <BulkBar
                            count={selection.count}
                            fields={bulkFields}
                            busy={bulk.busy}
                            note={bulk.note}
                            onClear={selection.clear}
                            onApply={(set) => bulk.apply(set)}
                            onDelete={() =>
                                bulk.confirmed({
                                    title: t.common.remove,
                                    message: t.bulk.confirmRemove.replace("{count}", String(selection.count)),
                                    confirmLabel: t.common.remove,
                                    doneLabel: t.bulk.removed,
                                })
                            }
                        />
                    ) : (
                        <Paginator
                            page={page}
                            total={units.data?.total ?? 0}
                            count={units.data?.units.length ?? 0}
                            onPage={(next) => apply({ page: next === 1 ? null : String(next) })}
                        />
                    )}
                </>
            )}

            {/* --- Who wants it: the pipeline's own card, filtered to this development ------- */}
            {tab === "interests" && (
                <div className="project-screen__grid">
                    {leads.loading && <p className="project-screen__state">{t.common.loading}</p>}
                    {!leads.loading && !leads.data?.leads.length && (
                        <EmptyState
                            span
                            icon="crm"
                            title={t.crm.emptyProjectLeads}
                            description={t.crm.emptyProjectLeadsHint}
                            actionLabel={t.crm.addLead}
                            onAction={openAddLead}
                        />
                    )}
                    {(leads.data?.leads ?? []).map((lead) => (
                        <LeadCard key={lead._id} lead={lead} onOpen={() => navigate(`/crm/leads/${lead._id}`)} />
                    ))}
                </div>
            )}

            {/* --- Buildings: a grid like the units, because they are the same kind of thing --- */}
            {tab === "buildings" && (
                <>
                    {!record.buildings.length && (
                        <EmptyState
                            icon="building"
                            title={t.properties.emptyBuildings}
                            description={t.properties.emptyBuildingsHint}
                            actionLabel={t.properties.addBuilding}
                            onAction={openAddBuilding}
                        />
                    )}

                    <div className="project-screen__blocks">
                        {record.buildings.map((building) => (
                            <article key={building._id} className="project-screen__block">
                                <span className="project-screen__block-icon">
                                    <Icon name="building" size={22} />
                                </span>
                                <span className="project-screen__block-name">{building.name}</span>

                                <div className="project-screen__block-actions">
                                    <button
                                        type="button"
                                        className="project-screen__block-action"
                                        onClick={() =>
                                            addComp({
                                                name: "rename-building",
                                                Component: RenamePopup,
                                                anim: "scale",
                                                details: {
                                                    title: t.properties.renameBuilding,
                                                    value: building.name,
                                                    path: `/properties/projects/${record._id}/buildings/${building._id}`,
                                                    onDone: refresh,
                                                },
                                            })
                                        }
                                        aria-label={t.common.edit}
                                    >
                                        <Icon name="edit" size={15} />
                                    </button>

                                    <button
                                        type="button"
                                        className="project-screen__block-action project-screen__block-action--danger"
                                        onClick={() =>
                                            addComp({
                                                name: "confirm",
                                                Component: ConfirmPopup,
                                                anim: "scale",
                                                details: {
                                                    title: t.common.remove,
                                                    message: t.properties.confirmRemoveBuilding.replace("{name}", building.name),
                                                    confirmLabel: t.common.remove,
                                                    onConfirm: () =>
                                                        action.invoke({
                                                            method: "DELETE",
                                                            path: `/properties/projects/${record._id}/buildings/${building._id}`,
                                                        }),
                                                },
                                            })
                                        }
                                        aria-label={t.common.remove}
                                    >
                                        <Icon name="trash" size={15} />
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                </>
            )}

            {/* --- Gallery and files: the record's own, from the drive, in one tab ------------ */}
            {tab === "files" && <RecordFiles ownerModel="Project" ownerId={record._id} onUpload={() => openUpload("Project", record._id)} />}

            {/* --- Details: the paperwork and the place, which is one of its details ---------- */}
            {tab === "details" && (
                <div className="project-screen__facts">
                    {facts.map((fact) => (
                        <div key={fact.key} className="project-screen__fact">
                            <span className="project-screen__fact-icon">
                                <Icon name={fact.icon} size={16} />
                            </span>
                            <span className="project-screen__fact-label">{fact.label}</span>
                            <strong className="project-screen__fact-value" dir={fact.dir}>
                                {fact.value}
                            </strong>
                        </div>
                    ))}

                    {/* No point drawing a map of nowhere. */}
                    {record.location && (
                        <div className="project-screen__map">
                            <Icon name="properties" size={26} />
                            <span>{t.properties.mapPlaceholder}</span>
                            <span className="project-screen__point">
                                {record.location.lat.toFixed(5)}, {record.location.lng.toFixed(5)}
                            </span>
                        </div>
                    )}

                    {description && <p className="project-screen__description">{description}</p>}

                    {/* Notes are written, not recorded: the box is a page's worth of room so it reads
                        like somewhere to think rather than another field with a value in it. */}
                    {record.notes && (
                        <section className="project-screen__notes">
                            <h2 className="project-screen__notes-title">{t.properties.fields.notes}</h2>
                            <p className="project-screen__notes-body">{record.notes}</p>
                        </section>
                    )}

                    {!hasDetails && <EmptyState span icon="document" title={t.properties.emptyDetails} description={t.properties.emptyDetailsHint} />}
                </div>
            )}
        </section>
    );
}
